/**
 * LaunchInputsForm — the start values a new run asks for (agion/Agion#2413).
 *
 * One field per value: the name made readable, the description under it,
 * required ones marked, prefilled by useLaunchInputs. A marked field says
 * what is wrong. It sits with the chat's message box and takes its look.
 */

import { LaunchFileInput } from '../launch/LaunchFileInput'
import { useTranslation } from 'react-i18next'
import { FormField, Input, Text, cn } from '@agion/ui'
import type { LaunchInputsForm as LaunchInputsFormState } from '../../hooks/useLaunchInputs'
import { humaniseName, isFileLaunchInput, type ValueProblem } from '../../utils/launchInputs'

/** What a marked field says, by why it is marked. */
const PROBLEM_TEXTS: Record<ValueProblem, { key: string; fallback: string }> = {
  file: { key: 'launchInputs.completeFile', fallback: 'Select a file whose upload has completed.' },
  missing: { key: 'launchInputs.enterValue', fallback: 'Enter a value to start the run.' },
  json: { key: 'launchInputs.invalidJson', fallback: 'Enter valid JSON, for example {"key": "value"} or [1, 2].' },
  number: { key: 'launchInputs.invalidNumber', fallback: 'Enter a number.' },
  integer: { key: 'launchInputs.invalidInteger', fallback: 'Enter a whole number.' },
  boolean: { key: 'launchInputs.invalidBoolean', fallback: 'Enter true or false.' },
}

interface LaunchInputsFormProps {
  form: LaunchInputsFormState
  disabled?: boolean
  className?: string
  /** Read only by the new chat's form (chatSurface LaunchInputsProps); this form is the same after a run. */
  afterRun?: boolean
}

export function LaunchInputsForm({ form, disabled, className }: LaunchInputsFormProps) {
  const { t } = useTranslation('chat')
  if (form.inputs.length === 0) return null

  return (
    <section
      data-testid="launch-inputs-form"
      aria-label={t('launchInputs.title', 'Start values')}
      className={cn(
        'w-full rounded-xl border px-4 py-3 text-left shadow-sm',
        'bg-white dark:bg-slate-800',
        Object.keys(form.marked).length > 0 ? 'border-rose-300 dark:border-rose-700' : 'border-slate-200 dark:border-slate-700',
        className,
      )}
    >
      <Text size="sm" weight="semibold" className="text-slate-700 dark:text-slate-300">
        {t('launchInputs.title', 'Start values')}
      </Text>
      <Text size="xs" color="muted" className="mb-3">
        {t('launchInputs.hint', 'The run starts with these values.')}
      </Text>
      <div className="grid gap-3 sm:grid-cols-2">
        {form.inputs.map((input) => {
          const id = `launch-input-${input.name}`
          const problem = form.marked[input.name]
          const marked = problem !== undefined
          return (
            <FormField
              key={input.name}
              id={id}
              label={humaniseName(input.name)}
              hint={input.description}
              required={input.required}
              error={problem ? t(PROBLEM_TEXTS[problem].key, PROBLEM_TEXTS[problem].fallback) : undefined}
            >
              {isFileLaunchInput(input) ? <LaunchFileInput input={input} form={form} id={id} disabled={disabled} /> : <Input
                id={id}
                name={input.name}
                size="sm"
                value={form.values[input.name] ?? ''}
                onChange={(e) => form.setValue(input.name, e.target.value)}
                hasError={marked}
                aria-required={input.required}
                disabled={disabled}
              />}
            </FormField>
          )
        })}
      </div>
    </section>
  )
}
